import { threadEntryAnchor, type TicketEventEntry, type TicketThreadEntry } from '@easyhelp/shared';

/**
 * Raggruppamento degli eventi di sistema nel thread (#120, decisione del product owner del
 * 16/10/2026): due o più eventi consecutivi diventano una riga compatta ed espandibile, così
 * risaltano i messaggi tra operatore e segnalante. Un evento isolato resta una riga sola.
 */

/** Messaggi (segnalazione, repliche, risposte, note): sempre in evidenza, mai raggruppati. */
export type TicketThreadMessage = Exclude<TicketThreadEntry, TicketEventEntry>;

/** Una riga del thread: un messaggio, un evento isolato o un gruppo di eventi consecutivi. */
export type ThreadItem =
  | { kind: 'messaggio'; entry: TicketThreadMessage }
  | { kind: 'evento'; entry: TicketEventEntry }
  /** `id` = id del primo evento: stabile quando altri eventi si aggiungono in fondo al gruppo. */
  | { kind: 'gruppo'; id: string; events: readonly TicketEventEntry[] };

/**
 * Le voci in ordine cronologico, con gli eventi consecutivi raccolti: il gruppo si chiude al primo
 * messaggio o nota. Da due eventi in su è un gruppo; uno solo resta un evento.
 */
export function groupThreadEntries(entries: readonly TicketThreadEntry[]): ThreadItem[] {
  const items: ThreadItem[] = [];
  let run: TicketEventEntry[] = [];
  const closeRun = () => {
    const [first] = run;
    if (first) {
      items.push(run.length === 1 ? { kind: 'evento', entry: first } : { kind: 'gruppo', id: first.id, events: run });
    }
    run = [];
  };
  for (const entry of entries) {
    if (entry.kind === 'evento') {
      run.push(entry);
    } else {
      closeRun();
      items.push({ kind: 'messaggio', entry });
    }
  }
  closeRun();
  return items;
}

/**
 * Il gruppo che contiene la voce con l'ancora (link delle notifiche, #70): va aperto perché la
 * voce sia visibile prima che ci si scorra sopra. `null` se l'ancora non è un evento raggruppato.
 */
export function groupContainingAnchor(items: readonly ThreadItem[], anchor: string | undefined): string | null {
  if (!anchor) return null;
  const group = items.find(
    (item) => item.kind === 'gruppo' && item.events.some((event) => threadEntryAnchor(event) === anchor)
  );
  return group?.kind === 'gruppo' ? group.id : null;
}

const ROME = 'Europe/Rome';
const time = new Intl.DateTimeFormat('it-IT', { hour: '2-digit', minute: '2-digit', timeZone: ROME });
const day = new Intl.DateTimeFormat('it-IT', { day: '2-digit', month: '2-digit', timeZone: ROME });

/** L'intervallo del gruppo, a vista e per i lettori di schermo. */
export interface GroupInterval {
  /** «16:14–16:18»; nello stesso minuto «16:14»; su più giorni «30/09 16:14 – 01/10 09:00». */
  visible: string;
  /** «dalle 16:14 alle 16:18», «alle 16:14», «dal 30/09 alle 16:14 al 01/10 alle 09:00». */
  spoken: string;
}

/** Dal primo all'ultimo evento del gruppo, nel fuso di Roma come il resto della console. */
export function groupInterval(events: readonly TicketEventEntry[]): GroupInterval {
  const first = new Date(events[0]!.at);
  const last = new Date(events.at(-1)!.at);
  const [fromTime, toTime] = [time.format(first), time.format(last)];
  const [fromDay, toDay] = [day.format(first), day.format(last)];
  if (fromDay !== toDay) {
    return {
      visible: `${fromDay} ${fromTime} – ${toDay} ${toTime}`,
      spoken: `dal ${fromDay} alle ${fromTime} al ${toDay} alle ${toTime}`,
    };
  }
  if (fromTime === toTime) return { visible: fromTime, spoken: `alle ${fromTime}` };
  return { visible: `${fromTime}–${toTime}`, spoken: `dalle ${fromTime} alle ${toTime}` };
}
